import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../api';
import { PRESTATION_ACTIVITIES, EXPENSE_TYPES } from '../constants';
import { openPrestationDocument, getIssuer, saveIssuer, DEFAULT_ISSUER } from '../lib/prestationDoc';

// TVA suggérée selon le client (aligné sur Facturation)
function tvaForClient(client) {
  if (!client) return 21;
  if (client.type === 'societe' && client.country !== 'ES') return 0;
  return 21;
}

function fmtMoney(n) {
  return Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}
function fmtHours(n) {
  const v = Number(n || 0);
  return v.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) + ' h';
}
function fmtDate(d) {
  if (!d) return '';
  return new Date(d + 'T00:00').toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function firstOfMonth() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toLocaleDateString('en-CA');
}
function lastOfMonth() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).toLocaleDateString('en-CA');
}
function todayStr() {
  return new Date().toLocaleDateString('en-CA');
}

function emptyForm(kind = 'temps') {
  return {
    kind,
    presta_date: todayStr(),
    client_id: '',
    dossier: '',
    dossier_number: '',
    activity: '',
    description: '',
    private_note: '',
    hours: '',
    hourly_rate: localStorage.getItem('presta_default_rate') || '',
    amount: '',
    frais_qty: '1',   // quantité (frais)
    frais_unit: '',   // prix unitaire (frais)
    billable: true,
  };
}

export default function PrestationsPage() {
  const [prestations, setPrestations] = useState([]);
  const [clients, setClients] = useState([]);
  const [dossiers, setDossiers] = useState([]);
  const [dossierInfos, setDossierInfos] = useState([]); // [{name, dossier_number, client_id}]
  const [customActivities, setCustomActivities] = useState([]); // activités ajoutées par l'utilisatrice
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  // Filtres
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(lastOfMonth());
  const [fClient, setFClient] = useState('');
  const [fDossier, setFDossier] = useState('');
  const [fKind, setFKind] = useState('');       // '', 'temps', 'frais'
  const [fStatus, setFStatus] = useState('');   // '', 'non_facturee', 'facturee'
  const [groupBy, setGroupBy] = useState(() => localStorage.getItem('presta_group_by') || 'dossier');

  // Sélection pour facturation
  const [selected, setSelected] = useState(() => new Set());

  // Modale
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());

  // Réglages émetteur (KDLEX par défaut)
  const [showIssuer, setShowIssuer] = useState(false);
  const [issuer, setIssuer] = useState(getIssuer());

  useEffect(() => { localStorage.setItem('presta_group_by', groupBy); }, [groupBy]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (from) params.from = from;
      if (to) params.to = to;
      if (fClient) params.client_id = fClient;
      if (fDossier) params.dossier = fDossier;
      if (fKind) params.kind = fKind;
      if (fStatus) params.status = fStatus;

      const [pRes, cRes, dRes, sRes, diRes, aRes] = await Promise.all([
        api.get('/prestations', { params }),
        api.get('/clients'),
        api.get('/prestations/meta/dossiers'),
        api.get('/prestations/stats'),
        api.get('/dossiers'),
        api.get('/activities'),
      ]);
      setPrestations(pRes.data);
      setClients(cRes.data);
      setDossiers(dRes.data);
      setDossierInfos(diRes.data);
      setCustomActivities(aRes.data);
      setStats(sRes.data);
      setSelected(new Set());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [from, to, fClient, fDossier, fKind, fStatus]);

  useEffect(() => { load(); }, [load]);

  // ---------- Saisie ----------
  const openCreate = (kind) => {
    setEditingId(null);
    setForm(emptyForm(kind));
    setShowModal(true);
  };

  const openEdit = (p) => {
    if (p.status === 'facturee') return; // non éditable si facturée
    setEditingId(p.id);
    setForm({
      kind: p.kind,
      presta_date: p.presta_date || todayStr(),
      client_id: p.client_id || '',
      dossier: p.dossier || '',
      dossier_number: (dossierInfos.find(x => x.name === p.dossier)?.dossier_number) || '',
      activity: p.activity || '',
      description: p.description || '',
      private_note: p.private_note || '',
      hours: p.hours || '',
      hourly_rate: p.hourly_rate || '',
      amount: p.amount || '',
      frais_qty: (p.kind === 'frais' && p.hours) ? p.hours : '1',
      frais_unit: (p.kind === 'frais' && p.hourly_rate) ? p.hourly_rate : '',
      billable: p.billable !== false,
    });
    setShowModal(true);
  };

  const handleClientChange = (clientId) => {
    const client = clients.find(c => c.id === parseInt(clientId));
    let rate = form.hourly_rate;
    // Pré-remplir le taux depuis le client s'il en a un et qu'on n'a pas encore saisi de taux
    if (client && client.hourly_rate && (!form.hourly_rate || form.hourly_rate === '')) {
      rate = client.hourly_rate;
    }
    setForm({ ...form, client_id: clientId, hourly_rate: rate });
  };

  // Numéro connu d'un dossier (depuis le répertoire)
  const dossierNumberOf = (name) => dossierInfos.find(x => x.name === name)?.dossier_number || '';
  // Table { nom dossier -> numéro } pour les documents PDF
  const dossierNumMap = () => Object.fromEntries(dossierInfos.filter(d => d.dossier_number).map(d => [d.name, d.dossier_number]));

  // Liste unifiée et dédoublonnée des dossiers (répertoire Dossiers + prestations/tâches)
  const dossierOptions = useMemo(() => {
    const set = new Set([...(dossiers || []), ...dossierInfos.map(d => d.name)].filter(Boolean));
    return [...set].sort((a, b) => a.localeCompare(b, 'fr'));
  }, [dossiers, dossierInfos]);

  // Types d'activités : liste B4T + activités ajoutées par l'utilisatrice
  const activityOptions = useMemo(() => {
    const set = new Set([...PRESTATION_ACTIVITIES, ...customActivities.filter(a => a.kind !== 'frais').map(a => a.name)].filter(Boolean));
    return [...set].sort((a, b) => a.localeCompare(b, 'fr'));
  }, [customActivities]);

  // Quand on choisit un type de frais : pré-remplir le montant (et la description si vide)
  const handleExpenseTypeChange = (name) => {
    const t = EXPENSE_TYPES.find(x => x.name === name);
    setForm(f => ({
      ...f,
      activity: name,
      description: (!f.description || f.description === '') ? name : f.description,
      frais_unit: (t && t.amount != null) ? String(t.amount) : f.frais_unit,
    }));
  };

  // Quand on change le dossier : auto-remplir son numéro, son client et son tarif horaire
  const handleDossierChange = (name) => {
    const info = dossierInfos.find(x => x.name === name);
    setForm(f => {
      const newClientId = (info?.client_id && !f.client_id) ? info.client_id : f.client_id;
      let rate = f.hourly_rate;
      if (info?.hourly_rate) {
        // Tarif du dossier = prioritaire (elle l'a configuré exprès)
        rate = info.hourly_rate;
      } else if (!f.hourly_rate || f.hourly_rate === '') {
        // Sinon, à défaut, le tarif du client lié
        const cli = clients.find(c => c.id === parseInt(newClientId));
        if (cli && cli.hourly_rate) rate = cli.hourly_rate;
      }
      return {
        ...f,
        dossier: name,
        dossier_number: info?.dossier_number || f.dossier_number,
        client_id: newClientId,
        hourly_rate: rate,
      };
    });
  };

  const save = async (e) => {
    e.preventDefault();
    if (form.kind === 'temps') {
      // Mémoriser le taux comme défaut
      if (form.hourly_rate) localStorage.setItem('presta_default_rate', String(form.hourly_rate));
    }
    const payload = {
      kind: form.kind,
      presta_date: form.presta_date,
      client_id: form.client_id || null,
      dossier: form.dossier ? form.dossier.trim() : null,
      activity: form.activity || null,
      description: form.description || null,
      private_note: form.private_note || null,
      hours: form.kind === 'temps' ? (parseFloat(form.hours) || 0) : (parseFloat(form.frais_qty) || 1),
      hourly_rate: form.kind === 'temps' ? (parseFloat(form.hourly_rate) || 0) : (parseFloat(form.frais_unit) || 0),
      amount: form.kind === 'frais'
        ? ((parseFloat(form.frais_unit) || 0) > 0
            ? +(((parseFloat(form.frais_qty) || 1)) * (parseFloat(form.frais_unit) || 0)).toFixed(2)
            : (parseFloat(form.amount) || 0))
        : null,
      billable: form.billable,
    };
    try {
      if (editingId) await api.put(`/prestations/${editingId}`, payload);
      else await api.post('/prestations', payload);
      // NB : on ne crée plus de dossier automatiquement ici — les dossiers se gèrent
      // dans la page « Dossiers » (numéro + client). On évite ainsi les dossiers fantômes.
      // Mémoriser une nouvelle activité si elle ne fait pas déjà partie de la liste
      const act = (form.activity || '').trim();
      if (form.kind === 'temps' && act && !activityOptions.includes(act)) {
        try { await api.post('/activities', { name: act, kind: 'temps' }); } catch { /* non bloquant */ }
      }
      setShowModal(false);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer cette prestation ?')) return;
    await api.delete(`/prestations/${id}`);
    load();
  };

  const unlink = async (p) => {
    if (!window.confirm('Détacher cette prestation de sa facture ? Elle repassera en « non facturée ». (La facture n\'est pas modifiée automatiquement.)')) return;
    await api.patch(`/prestations/${p.id}/unlink`);
    load();
  };

  // ---------- Sélection & facturation ----------
  const toggleSelect = (p) => {
    if (p.status === 'facturee') return;
    const next = new Set(selected);
    if (next.has(p.id)) next.delete(p.id); else next.add(p.id);
    setSelected(next);
  };

  const selectableInGroup = (items) => items.filter(p => p.status === 'non_facturee');

  const toggleSelectGroup = (items) => {
    const sel = selectableInGroup(items);
    const allSelected = sel.length > 0 && sel.every(p => selected.has(p.id));
    const next = new Set(selected);
    sel.forEach(p => { if (allSelected) next.delete(p.id); else next.add(p.id); });
    setSelected(next);
  };

  const selectedRows = prestations.filter(p => selected.has(p.id));
  const selectedTotal = selectedRows.reduce((s, p) => s + parseFloat(p.amount || 0), 0);
  const selectedClients = [...new Set(selectedRows.map(p => p.client_id).filter(v => v != null))];

  const createInvoice = async () => {
    if (selectedRows.length === 0) return;
    if (selectedClients.length > 1) {
      alert('Les prestations sélectionnées concernent plusieurs clients. Sélectionnez un seul client à la fois pour créer une facture.');
      return;
    }
    if (!window.confirm(`Créer une facture (brouillon) à partir de ${selectedRows.length} prestation(s) pour un total HT de ${fmtMoney(selectedTotal)} ?\n\nLa facture apparaîtra dans Facturation, prête à être émise.`)) return;
    try {
      const r = await api.post('/prestations/create-invoice', { presta_ids: selectedRows.map(p => p.id) });
      alert(`✅ Facture créée en brouillon (${r.data.count} prestations, ${fmtMoney(r.data.invoice.amount_ttc)} TTC).\nRetrouvez-la dans l'onglet Facturation pour l'émettre.`);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  // ---------- Regroupement ----------
  const groups = useMemo(() => {
    const map = {};
    prestations.forEach(p => {
      let key;
      if (groupBy === 'client') key = p.client_name || '(Sans client)';
      else if (groupBy === 'date') key = p.presta_date || '(Sans date)';
      else key = p.dossier || '(Sans dossier)';
      if (!map[key]) map[key] = [];
      map[key].push(p);
    });
    const emoji = groupBy === 'client' ? '👥' : groupBy === 'date' ? '📅' : '📁';
    return Object.keys(map).sort((a, b) => {
      if (a.startsWith('(Sans')) return 1;
      if (b.startsWith('(Sans')) return -1;
      if (groupBy === 'date') return b.localeCompare(a);
      return a.localeCompare(b, 'fr');
    }).map(k => ({
      label: groupBy === 'date' ? fmtDate(k) : k,
      emoji,
      items: map[k],
    }));
  }, [prestations, groupBy]);

  // Totaux période (sur la liste filtrée affichée)
  const totals = useMemo(() => {
    const t = { hours: 0, temps: 0, frais: 0 };
    prestations.forEach(p => {
      if (p.kind === 'temps') { t.hours += parseFloat(p.hours || 0); t.temps += parseFloat(p.amount || 0); }
      else t.frais += parseFloat(p.amount || 0);
    });
    return t;
  }, [prestations]);

  // ---------- Raccourcis de période ----------
  const setPeriod = (which) => {
    const now = new Date();
    if (which === 'month') { setFrom(firstOfMonth()); setTo(lastOfMonth()); }
    else if (which === 'lastmonth') {
      const f = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const l = new Date(now.getFullYear(), now.getMonth(), 0);
      setFrom(f.toLocaleDateString('en-CA')); setTo(l.toLocaleDateString('en-CA'));
    } else if (which === 'year') {
      setFrom(new Date(now.getFullYear(), 0, 1).toLocaleDateString('en-CA'));
      setTo(new Date(now.getFullYear(), 11, 31).toLocaleDateString('en-CA'));
    } else if (which === 'all') { setFrom(''); setTo(''); }
  };

  // ---------- Export CSV ----------
  const exportCSV = () => {
    const header = ['Date', 'Type', 'Client', 'Dossier', 'Activité', 'Description', 'Heures', 'Taux (€/h)', 'Montant HT (€)', 'Facturable', 'Statut'];
    const esc = (v) => {
      const s = String(v == null ? '' : v).replace(/"/g, '""');
      return `"${s}"`;
    };
    const lines = prestations.map(p => [
      fmtDate(p.presta_date),
      p.kind === 'temps' ? 'Prestation' : 'Frais',
      p.client_name || '',
      p.dossier || '',
      p.activity || '',
      (p.description || '').replace(/\n/g, ' '),
      p.kind === 'temps' ? String(p.hours || '').replace('.', ',') : '',
      p.kind === 'temps' ? String(p.hourly_rate || '').replace('.', ',') : '',
      String(p.amount || 0).replace('.', ','),
      p.billable ? 'Oui' : 'Non',
      p.status === 'facturee' ? 'Facturée' : 'Non facturée',
    ].map(esc).join(';'));
    const csv = '﻿' + [header.map(esc).join(';'), ...lines].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `prestations_${from || 'debut'}_${to || 'fin'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ---------- Relevé de prestations (PDF format KDLEX) ----------
  const printReport = () => {
    const clientObj = fClient ? (clients.find(c => c.id === parseInt(fClient)) || null) : null;
    openPrestationDocument({
      kind: 'releve',
      prestations,
      client: clientObj,
      issuer,
      dossierNumbers: dossierNumMap(),
      meta: {
        from, to,
        date: todayStr(),
        subject: fDossier || undefined,
        tvaRate: clientObj ? tvaForClient(clientObj) : 21,
      },
    });
  };

  // ---------- Aperçu facture détaillée (PDF) depuis la sélection ----------
  const previewInvoice = () => {
    if (selectedRows.length === 0) return;
    if (selectedClients.length > 1) {
      alert('Sélectionnez les prestations d\'un seul client pour prévisualiser une facture.');
      return;
    }
    const clientObj = selectedClients[0] ? (clients.find(c => c.id === selectedClients[0]) || null) : null;
    openPrestationDocument({
      kind: 'facture',
      prestations: selectedRows,
      client: clientObj,
      issuer,
      dossierNumbers: dossierNumMap(),
      meta: {
        date: todayStr(),
        tvaRate: clientObj ? tvaForClient(clientObj) : 21,
      },
    });
  };

  const saveIssuerSettings = () => {
    saveIssuer(issuer);
    setShowIssuer(false);
  };

  // Calcul live du montant dans la modale
  const fraisUnit = parseFloat(form.frais_unit) || 0;
  const fraisQty = parseFloat(form.frais_qty) || 0;
  const liveAmount = form.kind === 'temps'
    ? (parseFloat(form.hours) || 0) * (parseFloat(form.hourly_rate) || 0)
    : (fraisUnit > 0 ? (fraisQty || 1) * fraisUnit : (parseFloat(form.amount) || 0));

  return (
    <div>
      <div className="page-header page-header-row">
        <div>
          <h1>Prestations</h1>
          <p>Encodez votre temps et vos frais, puis générez vos factures</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={() => { setIssuer(getIssuer()); setShowIssuer(true); }} title="Coordonnées de l'émetteur imprimées sur les documents">⚙️ Émetteur</button>
          <button className="btn btn-secondary" onClick={() => openCreate('frais')}>+ Frais</button>
          <button className="btn btn-primary" onClick={() => openCreate('temps')}>+ Prestation</button>
        </div>
      </div>

      {/* Fenêtre : mon temps du jour et de la semaine (comme B4T) */}
      <div className="card" style={{ padding: 0, marginBottom: 16, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
          <div style={{ padding: '14px 20px', borderRight: '1px solid var(--border-light)' }}>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>⏱️ Aujourd'hui</div>
            <div style={{ fontSize: '1.9rem', fontWeight: 800, color: 'var(--primary)', lineHeight: 1.1 }}>{fmtHours(stats.hours_today)}</div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-light)' }}>{fmtMoney(stats.temps_today)} d'honoraires</div>
          </div>
          <div style={{ padding: '14px 20px' }}>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>📅 Cette semaine</div>
            <div style={{ fontSize: '1.9rem', fontWeight: 800, lineHeight: 1.1 }}>{fmtHours(stats.hours_week)}</div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-light)' }}>{fmtMoney(stats.temps_week)} d'honoraires</div>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 18 }}>
        <div className="stat-card" style={{ textAlign: 'left' }}>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>⏱️ Heures ce mois</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 800 }}>{fmtHours(stats.hours_month)}</div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-light)' }}>{fmtMoney(stats.temps_month)}</div>
        </div>
        <div className="stat-card" style={{ textAlign: 'left' }}>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>💼 Honoraires à facturer</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--primary)' }}>{fmtMoney(stats.temps_a_facturer)}</div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-light)' }}>{fmtHours(stats.hours_a_facturer)} non facturées</div>
        </div>
        <div className="stat-card" style={{ textAlign: 'left' }}>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>💶 Frais à facturer</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--warning)' }}>{fmtMoney(stats.frais_a_facturer)}</div>
        </div>
        <div className="stat-card" style={{ textAlign: 'left' }}>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>📊 Total à facturer</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--success)' }}>{fmtMoney((parseFloat(stats.temps_a_facturer) || 0) + (parseFloat(stats.frais_a_facturer) || 0))}</div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-light)' }}>{stats.nb_a_facturer || 0} ligne(s)</div>
        </div>
      </div>

      {/* Filtres période */}
      <div className="card" style={{ padding: 14, marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label style={{ fontSize: '0.75rem' }}>Du</label>
            <input type="date" className="form-input" value={from} onChange={e => setFrom(e.target.value)} style={{ maxWidth: 160 }} />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label style={{ fontSize: '0.75rem' }}>Au</label>
            <input type="date" className="form-input" value={to} onChange={e => setTo(e.target.value)} style={{ maxWidth: 160 }} />
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setPeriod('month')}>Ce mois</button>
            <button className="btn btn-secondary btn-sm" onClick={() => setPeriod('lastmonth')}>Mois dernier</button>
            <button className="btn btn-secondary btn-sm" onClick={() => setPeriod('year')}>Cette année</button>
            <button className="btn btn-secondary btn-sm" onClick={() => setPeriod('all')}>Tout</button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 12 }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label style={{ fontSize: '0.75rem' }}>Client</label>
            <select className="form-input" value={fClient} onChange={e => setFClient(e.target.value)} style={{ maxWidth: 220 }}>
              <option value="">Tous</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label style={{ fontSize: '0.75rem' }}>Dossier</label>
            <input className="form-input" value={fDossier} onChange={e => setFDossier(e.target.value)} list="presta-dossiers-filter" placeholder="Tous" style={{ maxWidth: 200 }} />
            <datalist id="presta-dossiers-filter">{dossierOptions.map(d => <option key={d} value={d} />)}</datalist>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label style={{ fontSize: '0.75rem' }}>Type</label>
            <select className="form-input" value={fKind} onChange={e => setFKind(e.target.value)} style={{ maxWidth: 150 }}>
              <option value="">Tout</option>
              <option value="temps">⏱️ Prestations</option>
              <option value="frais">💶 Frais</option>
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label style={{ fontSize: '0.75rem' }}>Statut</label>
            <select className="form-input" value={fStatus} onChange={e => setFStatus(e.target.value)} style={{ maxWidth: 170 }}>
              <option value="">Tous</option>
              <option value="non_facturee">Non facturées</option>
              <option value="facturee">Facturées</option>
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label style={{ fontSize: '0.75rem' }}>Grouper par</label>
            <select className="form-input" value={groupBy} onChange={e => setGroupBy(e.target.value)} style={{ maxWidth: 150 }}>
              <option value="dossier">📁 Dossier</option>
              <option value="client">👥 Client</option>
              <option value="date">📅 Date</option>
            </select>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <button className="btn btn-secondary btn-sm" onClick={printReport} title="Imprimer / PDF">🖨️ Imprimer</button>
            <button className="btn btn-secondary btn-sm" onClick={exportCSV} title="Exporter en CSV (Excel)">⬇️ CSV</button>
          </div>
        </div>

        {/* Totaux période */}
        <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--border-light)', display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: '0.82rem' }}>
          <span><strong>{fmtHours(totals.hours)}</strong> <span style={{ color: 'var(--text-muted)' }}>de temps</span></span>
          <span><strong>{fmtMoney(totals.temps)}</strong> <span style={{ color: 'var(--text-muted)' }}>honoraires HT</span></span>
          <span><strong>{fmtMoney(totals.frais)}</strong> <span style={{ color: 'var(--text-muted)' }}>frais HT</span></span>
          <span style={{ marginLeft: 'auto' }}><strong style={{ color: 'var(--success)' }}>{fmtMoney(totals.temps + totals.frais)}</strong> <span style={{ color: 'var(--text-muted)' }}>total HT sur la période</span></span>
        </div>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="empty-state"><p>Chargement…</p></div>
      ) : prestations.length === 0 ? (
        <div className="empty-state">
          <div className="empty-emoji">⏱️</div>
          <p>Aucune prestation sur cette période. Cliquez sur « + Prestation » pour commencer.</p>
        </div>
      ) : (
        <div style={{ paddingBottom: selectedRows.length > 0 ? 80 : 0 }}>
          {groups.map(group => {
            const sel = selectableInGroup(group.items);
            const allSel = sel.length > 0 && sel.every(p => selected.has(p.id));
            const gHours = group.items.reduce((s, p) => s + (p.kind === 'temps' ? parseFloat(p.hours || 0) : 0), 0);
            const gAmount = group.items.reduce((s, p) => s + parseFloat(p.amount || 0), 0);
            return (
              <div key={group.label} style={{ marginBottom: 20 }}>
                <div className="day-section-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {sel.length > 0 && (
                    <input type="checkbox" checked={allSel} onChange={() => toggleSelectGroup(group.items)} title="Tout sélectionner dans ce groupe" style={{ cursor: 'pointer' }} />
                  )}
                  <span className="section-emoji">{group.emoji}</span>
                  {group.label}
                  <span style={{ marginLeft: 'auto', fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 500 }}>
                    {fmtHours(gHours)} · {fmtMoney(gAmount)} · {group.items.length} ligne(s)
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {group.items.map(p => (
                    <PrestaRow
                      key={p.id} p={p}
                      selected={selected.has(p.id)}
                      onToggle={toggleSelect}
                      onEdit={openEdit}
                      onDelete={remove}
                      onUnlink={unlink}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Barre flottante de facturation */}
      {selectedRows.length > 0 && (
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, background: 'var(--card-bg, #fff)',
          borderTop: '2px solid var(--primary)', boxShadow: '0 -4px 16px rgba(0,0,0,0.12)',
          padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 16, zIndex: 50, flexWrap: 'wrap',
        }}>
          <div style={{ fontWeight: 700 }}>
            {selectedRows.length} prestation(s) sélectionnée(s) · {fmtMoney(selectedTotal)} HT
            {selectedClients.length > 1 && <span style={{ color: 'var(--danger)', marginLeft: 10, fontWeight: 600 }}>⚠️ Plusieurs clients — sélectionnez un seul client</span>}
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary" onClick={() => setSelected(new Set())}>Annuler</button>
            <button className="btn btn-secondary" onClick={previewInvoice} disabled={selectedClients.length > 1} title="Prévisualiser / imprimer la facture détaillée en PDF">📄 Aperçu PDF</button>
            <button className="btn btn-primary" onClick={createInvoice} disabled={selectedClients.length > 1}>🧾 Créer la facture</button>
          </div>
        </div>
      )}

      {/* Modale saisie */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <h2 style={{ margin: 0 }}>
                {editingId ? 'Modifier' : 'Nouvelle'} {form.kind === 'frais' ? 'dépense / frais' : 'prestation'}
              </h2>
              <button type="button" className="btn-icon" onClick={() => setShowModal(false)}>✕</button>
            </div>

            {/* Bascule type */}
            <div className="task-filters" style={{ marginBottom: 14 }}>
              <button type="button" className={`filter-btn ${form.kind === 'temps' ? 'active' : ''}`} onClick={() => setForm({ ...form, kind: 'temps' })}>⏱️ Temps passé</button>
              <button type="button" className={`filter-btn ${form.kind === 'frais' ? 'active' : ''}`} onClick={() => setForm({ ...form, kind: 'frais' })}>💶 Frais / débours</button>
            </div>

            <form onSubmit={save}>
              <div className="form-row">
                <div className="form-group">
                  <label>Date *</label>
                  <input type="date" className="form-input" value={form.presta_date} onChange={e => setForm({ ...form, presta_date: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Client</label>
                  <select className="form-input" value={form.client_id} onChange={e => handleClientChange(e.target.value)}>
                    <option value="">— Sélectionner —</option>
                    {clients.map(c => (
                      <option key={c.id} value={c.id}>{c.type === 'societe' ? '🏢' : '👤'} {c.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group" style={{ flex: 2 }}>
                  <label>Dossier</label>
                  <input className="form-input" value={form.dossier} onChange={e => handleDossierChange(e.target.value)} list="presta-dossiers" placeholder="Ex : Dupont c/ Martin" />
                  <datalist id="presta-dossiers">{dossierOptions.map(d => <option key={d} value={d} />)}</datalist>
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>N° dossier</label>
                  <input className="form-input" value={form.dossier_number} disabled placeholder="—" title="Le numéro vient de la fiche du dossier (page Dossiers)" style={{ background: 'var(--bg)', color: 'var(--text-muted)' }} />
                </div>
              </div>

              <div className="form-group">
                <label>{form.kind === 'temps' ? "Type d'activité" : 'Type de frais'}</label>
                <input className="form-input" value={form.activity}
                  onChange={e => form.kind === 'frais' ? handleExpenseTypeChange(e.target.value) : setForm({ ...form, activity: e.target.value })}
                  list={form.kind === 'temps' ? 'presta-activities' : 'expense-types'}
                  placeholder={form.kind === 'temps' ? 'Ex : Correspondance au client' : 'Ex : Frais de correspondance'} />
                <datalist id="presta-activities">{activityOptions.map(a => <option key={a} value={a} />)}</datalist>
                <datalist id="expense-types">{EXPENSE_TYPES.map(t => <option key={t.name} value={t.name} />)}</datalist>
                {form.kind === 'temps' && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>Choisis dans la liste (types Bill4Time) ou tape le tien — il sera mémorisé.</div>}
              </div>

              <div className="form-group">
                <label>Description {form.kind === 'temps' ? '(apparaîtra sur la facture)' : 'du frais'}</label>
                <textarea className="form-input" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} style={{ minHeight: 56 }}
                  placeholder={form.kind === 'temps' ? 'Ex : Étude du dossier et rédaction des conclusions' : 'Ex : Frais de greffe, déplacement, copies…'} />
              </div>

              {form.kind === 'temps' ? (
                <div className="form-row">
                  <div className="form-group">
                    <label>Durée (heures) *</label>
                    <input type="number" step="0.05" min="0" className="form-input" value={form.hours} onChange={e => setForm({ ...form, hours: e.target.value })} list="presta-durations" placeholder="Ex : 0.25" required />
                    <datalist id="presta-durations">
                      {['0.05','0.1','0.15','0.2','0.25','0.3','0.35','0.4','0.45','0.5','0.55','0.6','0.65','0.7','0.75','0.8','0.85','0.9','0.95','1','1.25','1.5','1.75','2','2.5','3','4'].map(v => <option key={v} value={v} />)}
                    </datalist>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 2 }}>Pas de 0,05 h · 0,1 h=6 min · 0,25 h=15 min · 0,5 h=30 min · 1 h=60 min</div>
                  </div>
                  <div className="form-group">
                    <label>Taux horaire (€ / h)</label>
                    <input type="number" step="0.01" min="0" className="form-input" value={form.hourly_rate} onChange={e => setForm({ ...form, hourly_rate: e.target.value })} placeholder="Ex : 150" />
                  </div>
                  <div className="form-group">
                    <label>Montant HT</label>
                    <input className="form-input" value={fmtMoney(liveAmount)} disabled style={{ fontWeight: 700, background: 'var(--primary-bg)', color: 'var(--primary)' }} />
                  </div>
                </div>
              ) : (
                <div className="form-row">
                  <div className="form-group">
                    <label>Prix unitaire (€)</label>
                    <input type="number" step="0.01" min="0" className="form-input" value={form.frais_unit} onChange={e => setForm({ ...form, frais_unit: e.target.value })} placeholder="Ex : 0.30" />
                  </div>
                  <div className="form-group">
                    <label>Quantité</label>
                    <input type="number" step="0.01" min="0" className="form-input" value={form.frais_qty} onChange={e => setForm({ ...form, frais_qty: e.target.value })} placeholder="Ex : 5" />
                  </div>
                  <div className="form-group">
                    <label>Montant HT</label>
                    <input className="form-input" value={fmtMoney(liveAmount)} disabled style={{ fontWeight: 700, background: 'var(--primary-bg)', color: 'var(--primary)' }} />
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>Note privée (interne, jamais facturée)</label>
                <textarea className="form-input" value={form.private_note} onChange={e => setForm({ ...form, private_note: e.target.value })} rows={2} style={{ minHeight: 48 }} />
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 600 }}>
                  <input type="checkbox" checked={form.billable} onChange={e => setForm({ ...form, billable: e.target.checked })} />
                  Facturable au client
                </label>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>Décochez pour un temps interne / non facturable (suivi seulement).</div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary">{editingId ? 'Enregistrer' : 'Ajouter'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modale réglages émetteur */}
      {showIssuer && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <h2 style={{ margin: 0 }}>Émetteur des documents</h2>
              <button type="button" className="btn-icon" onClick={() => setShowIssuer(false)}>✕</button>
            </div>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 0 }}>
              Ces coordonnées apparaissent en haut de tes factures et relevés PDF.
            </p>

            <div className="form-row">
              <div className="form-group">
                <label>Nom / Cabinet</label>
                <input className="form-input" value={issuer.name} onChange={e => setIssuer({ ...issuer, name: e.target.value })} />
              </div>
              <div className="form-group">
                <label>N° TVA</label>
                <input className="form-input" value={issuer.vat} onChange={e => setIssuer({ ...issuer, vat: e.target.value })} />
              </div>
            </div>

            <div className="form-group">
              <label>Adresse (une ligne par ligne)</label>
              <textarea className="form-input" rows={3} style={{ minHeight: 70 }}
                value={(issuer.address_lines || []).join('\n')}
                onChange={e => setIssuer({ ...issuer, address_lines: e.target.value.split('\n') })} />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Téléphone</label>
                <input className="form-input" value={issuer.phone} onChange={e => setIssuer({ ...issuer, phone: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input className="form-input" value={issuer.email} onChange={e => setIssuer({ ...issuer, email: e.target.value })} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>IBAN</label>
                <input className="form-input" value={issuer.iban} onChange={e => setIssuer({ ...issuer, iban: e.target.value })} />
              </div>
              <div className="form-group">
                <label>BIC</label>
                <input className="form-input" value={issuer.bic} onChange={e => setIssuer({ ...issuer, bic: e.target.value })} />
              </div>
            </div>

            <div className="form-group">
              <label>Mention de bas de page</label>
              <textarea className="form-input" rows={2} style={{ minHeight: 56 }}
                value={issuer.footer_note} onChange={e => setIssuer({ ...issuer, footer_note: e.target.value })} />
            </div>

            <div className="modal-actions" style={{ justifyContent: 'space-between' }}>
              <button type="button" className="btn btn-secondary" onClick={() => setIssuer({ ...DEFAULT_ISSUER })} title="Rétablir les coordonnées KDLEX par défaut">↺ Valeurs KDLEX</button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowIssuer(false)}>Annuler</button>
                <button type="button" className="btn btn-primary" onClick={saveIssuerSettings}>Enregistrer</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PrestaRow({ p, selected, onToggle, onEdit, onDelete, onUnlink }) {
  const isBilled = p.status === 'facturee';
  const accent = isBilled ? '#10b981' : '#f59e0b'; // vert = facturée, ambre = à facturer
  const tint = isBilled ? 'rgba(16,185,129,0.08)' : 'rgba(245,158,11,0.09)';
  return (
    <div className="task-item" style={{ cursor: isBilled ? 'default' : 'pointer', opacity: p.billable ? 1 : 0.7, borderLeft: `4px solid ${accent}`, background: tint }}>
      <div style={{ display: 'flex', alignItems: 'center', paddingRight: 4 }} onClick={e => e.stopPropagation()}>
        {!isBilled ? (
          <input type="checkbox" checked={selected} onChange={() => onToggle(p)} style={{ cursor: 'pointer', width: 18, height: 18 }} />
        ) : (
          <span title="Facturée" style={{ fontSize: '1rem' }}>🔒</span>
        )}
      </div>

      <div style={{ flex: 1, minWidth: 0 }} onClick={() => onEdit(p)}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>{fmtDate(p.presta_date)}</span>
          <span className="badge" style={{ background: p.kind === 'temps' ? '#eff6ff' : '#fff7ed', color: p.kind === 'temps' ? '#3b82f6' : '#f97316' }}>
            {p.kind === 'temps' ? '⏱️ ' + (p.activity || 'Prestation') : '💶 Frais'}
          </span>
          {!p.billable && <span className="badge" style={{ background: '#f1f5f9', color: '#64748b' }}>Non facturable</span>}
          {isBilled && <span className="badge" style={{ background: '#ecfdf5', color: '#10b981' }}>✅ Facturée{p.invoice_number ? ' · ' + p.invoice_number : ''}</span>}
        </div>
        <div style={{ fontSize: '0.8rem', color: 'var(--text-light)', marginTop: 3 }}>
          {p.client_name && <span>👥 {p.client_name} · </span>}
          {p.dossier && <span>📁 {p.dossier}</span>}
          {p.author_name && <span> · ✍️ {p.author_name}</span>}
        </div>
        {p.description && <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 3, whiteSpace: 'pre-wrap' }}>{p.description}</div>}
      </div>

      <div style={{ textAlign: 'right', minWidth: 110 }} onClick={() => onEdit(p)}>
        <div style={{ fontWeight: 700, fontSize: '0.98rem' }}>{fmtMoney(p.amount)}</div>
        {p.kind === 'temps' && (
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{fmtHours(p.hours)} × {fmtMoney(p.hourly_rate)}</div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 4, flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
        {isBilled ? (
          <button className="btn-ghost" onClick={() => onUnlink(p)} title="Détacher de la facture">🔗</button>
        ) : (
          <button className="btn-ghost" onClick={() => onDelete(p.id)} title="Supprimer">🗑️</button>
        )}
      </div>
    </div>
  );
}
